import { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { plateformesAPI } from "services/swagger-api";
import { PlateformeFormFieldNames } from "./PlateformeFormFieldNames";
import { PlateformeDto } from "api/plateforme";
import { themeFormConfig } from "./themeFormConfig";
import { ThemeFormValues } from "./ThemeFormValues";
import { ThemeLayout } from "./ThemeLayout";

interface ThemeEditFormProps {
  plateforme: PlateformeDto
  onSuccess?: () => void;
  refresh: () => void
}

export const ThemeEditForm: React.FC<ThemeEditFormProps> = ({
  plateforme,
  onSuccess,
  refresh
}) => {
  const formikRef = useRef<FormikProps<ThemeFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setPanelActions, closePanel } = usePanel();

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: "COMMON.VALIDATE",
        submitDisabled: !isFormValid,
        onSubmit: () => {
          formikRef.current?.submitForm();
        },
      });
    }
  }, [formikRef, setPanelActions, isFormValid]);

  const initialValues = useMemo(() => {
    if (!plateforme.theme) {
      return themeFormConfig.initialValues
    }
    return {
      [PlateformeFormFieldNames.PrimaryColor]: plateforme.theme.primaryColor,
      [PlateformeFormFieldNames.SecondaryColor]: plateforme.theme.secondaryColor,
      [PlateformeFormFieldNames.BackgroundColor]: plateforme.theme.backgroundColor,
      [PlateformeFormFieldNames.SurfaceColor]: plateforme.theme.surfaceColor,
      [PlateformeFormFieldNames.AccentColor]: plateforme.theme.accentColor,
      [PlateformeFormFieldNames.ErrorColor]: plateforme.theme.errorColor,
      [PlateformeFormFieldNames.SuccessColor]: plateforme.theme.successColor,
      [PlateformeFormFieldNames.WarningColor]: plateforme.theme.warningColor,
      [PlateformeFormFieldNames.InfoColor]: plateforme.theme.infoColor,
      [PlateformeFormFieldNames.FontFamily]: plateforme.theme.fontFamily,
    }
  }, [plateforme.theme])

  const onSubmit = async (values: ThemeFormValues) => {
    setIsLoading(true);
    try {
      await plateformesAPI.updatePlateformeById({
        plateformeId: plateforme.id,
        updatePlateformeCommand: {
          name: plateforme.name,
          theme: {
            primaryColor: values[PlateformeFormFieldNames.PrimaryColor],
            secondaryColor: values[PlateformeFormFieldNames.SecondaryColor],
            backgroundColor: values[PlateformeFormFieldNames.BackgroundColor],
            surfaceColor: values[PlateformeFormFieldNames.SurfaceColor],
            accentColor: values[PlateformeFormFieldNames.AccentColor],
            errorColor: values[PlateformeFormFieldNames.ErrorColor],
            successColor: values[PlateformeFormFieldNames.SuccessColor],
            warningColor: values[PlateformeFormFieldNames.WarningColor],
            infoColor: values[PlateformeFormFieldNames.InfoColor],
            fontFamily: values[PlateformeFormFieldNames.FontFamily],
          },
        },
      });
      onSuccess?.();
      refresh()
      closePanel();
    } catch (error) {
      console.error('Error on submit plateforme:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={themeFormConfig.validationSchema}
        onValidationChange={(isValid: boolean) => setIsFormValid(isValid)}
        onSubmit={onSubmit}
        innerRef={formikRef}
      >
        {({ isValid }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          return (
            <Form>
              <ThemeLayout />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
